import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProjectService } from '@core/projects/project.service';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Schützt die `projects/:projectId/:branchName`-Route: Wenn die projectId unbekannt ist,
 * wird der Benutzer zur Projektliste umgeleitet, anstatt eine leere Shell zu
 * rendern.
 *
 * `segments` sind die URL-Segmente, die gegen die Projekt-Route gematcht wurden.
 * Um robust gegenüber zukünftiger Routenverschachtelung zu bleiben, lokalisieren
 * wir das `projects`-Segment und lesen die projectId danach aus.
 */
export const projectExistsGuard: CanMatchFn = (_route: Route, segments: UrlSegment[]) => {
  const router = inject(Router);
  const projectService = inject(ProjectService);

  const projectsIndex = segments.findIndex(segment => segment.path === 'projects');
  const projectId = projectsIndex >= 0 ? (segments[projectsIndex + 1]?.path ?? '') : '';

  if (!UUID_PATTERN.test(projectId)) {
    return router.parseUrl('/projects');
  }

  // canMatch re-runs on every in-project navigation (not just first entry), so
  // use the cached get$() instead of a force-refetching call to avoid an extra
  // GitLab-backed round trip on every click.
  return projectService.get$(projectId).pipe(
    map(() => true),
    catchError((error: unknown) =>
      of(
        error instanceof HttpErrorResponse && error.status === 403
          ? router.createUrlTree(['/projects'], { queryParams: { accessDenied: 1 } })
          : router.parseUrl('/projects')
      )
    )
  );
};
